<template>
  <section class="MuniTransferDocs">
    <p class="hint">
      מגדיר אילו מסמכים נדרשים בהסבת פרימיום עבור עיריות שאינן מוגדרות ברשימת "עיריות הסבה מינימלית".
      לכל קבוצת מסמכים: אופציונלי / חובה / לא בשימוש, בנפרד ללקוח ולתייר.
    </p>

    <b-input
      class="search"
      icon="magnify"
      :icon-right="search ? 'close-circle' : ''"
      icon-right-clickable
      @icon-right-click="search = ''"
      v-model="search"
      placeholder="חיפוש עיריה..."
    />

    <b-table
      :data="filteredRows"
      :bordered="true"
      :striped="true"
      :narrowed="true"
      :hoverable="false"
      :loading="isLoading"
      :sticky-header="true"
    >
      <b-table-column centered field="MunicipalityName" label="עיריה" v-slot="props">
        {{ props.row.MunicipalityName }}
      </b-table-column>

      <b-table-column centered label="תצהיר (לקוח)" v-slot="props">
        <b-select v-model="props.row.ClientDeclaration">
          <option v-for="o in requirementOptions" :key="o.value" :value="o.value">{{ o.label }}</option>
        </b-select>
      </b-table-column>
      <b-table-column centered label="מסמכי חוזה (לקוח)" v-slot="props">
        <b-select v-model="props.row.ClientContractForms">
          <option v-for="o in requirementOptions" :key="o.value" :value="o.value">{{ o.label }}</option>
        </b-select>
      </b-table-column>
      <b-table-column centered label="מסמך מזהה (לקוח)" v-slot="props">
        <b-select v-model="props.row.ClientIdentityDocument">
          <option v-for="o in requirementOptions" :key="o.value" :value="o.value">{{ o.label }}</option>
        </b-select>
      </b-table-column>

      <b-table-column centered label="תצהיר (תייר)" v-slot="props">
        <b-select v-model="props.row.TouristDeclaration">
          <option v-for="o in requirementOptions" :key="o.value" :value="o.value">{{ o.label }}</option>
        </b-select>
      </b-table-column>
      <b-table-column centered label="מסמכי חוזה (תייר)" v-slot="props">
        <b-select v-model="props.row.TouristContractForms">
          <option v-for="o in requirementOptions" :key="o.value" :value="o.value">{{ o.label }}</option>
        </b-select>
      </b-table-column>
      <b-table-column centered label="מסמך מזהה (תייר)" v-slot="props">
        <b-select v-model="props.row.TouristIdentityDocument">
          <option v-for="o in requirementOptions" :key="o.value" :value="o.value">{{ o.label }}</option>
        </b-select>
      </b-table-column>

      <b-table-column centered label="" v-slot="props">
        <div class="row-actions">
          <b-button :loading="props.row.isSaving" @click="save(props.row)" type="is-primary">שמור</b-button>
          <b-button :loading="props.row.isDeleting" @click="remove(props.row)" type="is-danger">מחק</b-button>
        </div>
      </b-table-column>

      <template #empty>
        <div class="has-text-centered">{{ emptyMessage }}</div>
      </template>
    </b-table>

    <div class="add-row">
      <b-field label="הוסף עיריה" class="add-municipality">
        <FieldInlineSelect
          :hide-hover="true"
          :clearable="true"
          :filterable="true"
          :null-is-value="true"
          placeholder="בחר עיריה"
          :optionsAsync="getMunicipalities"
          :reduce="(municipality) => municipality"
          v-model="newMunicipality"
        />
      </b-field>
      <b-button :loading="isAdding" :disabled="!newMunicipality || !newMunicipality.Value" @click="add" type="is-primary">
        הוסף
      </b-button>
    </div>
  </section>
</template>

<script>
import ProfileService from "@/services/ProfileService";
import MunicipalitiesService from "@/services/MunicipalitiesService";
import FieldInlineSelect from "@/components/Fields/FieldInlineSelect.vue";

const REQUIREMENT_OPTIONS = [
  { value: 0, label: "אופציונלי" },
  { value: 1, label: "חובה" },
  { value: 2, label: "לא בשימוש" },
];
const NO_ROWS_MESSAGE = "לא הוגדרו מסמכי הסבה לפי עיריה.";
const NO_MATCH_MESSAGE = "לא נמצאו עיריות התואמות לחיפוש.";

export default {
  name: "ProfileMunicipalityTransferDocuments",
  components: { FieldInlineSelect },
  data() {
    return {
      requirementOptions: REQUIREMENT_OPTIONS,
      rows: [],
      search: "",
      newMunicipality: null,
      isLoading: false,
      isAdding: false,
    };
  },
  computed: {
    searchText() {
      return this.search.trim().toLowerCase();
    },
    filteredRows() {
      if (!this.searchText) return this.rows;
      return this.rows.filter((r) => (r.MunicipalityName || "").toLowerCase().includes(this.searchText));
    },
    emptyMessage() {
      return this.searchText ? NO_MATCH_MESSAGE : NO_ROWS_MESSAGE;
    },
  },
  created() {
    this.load();
  },
  methods: {
    getMunicipalities() {
      return MunicipalitiesService.get;
    },
    load() {
      this.isLoading = true;
      ProfileService.getMunicipalityTransferDocuments()
        .then((r) => {
          this.rows = (r.data || []).map((d) => ({
            ...d,
            isSaving: false,
            isDeleting: false,
          }));
        })
        .catch(() => this.showError("שגיאה בטעינת מסמכי ההסבה"))
        .finally(() => {
          this.isLoading = false;
        });
    },
    buildPayload(row) {
      return {
        MunicipalityId: row.MunicipalityId,
        ClientDeclaration: row.ClientDeclaration,
        ClientContractForms: row.ClientContractForms,
        ClientIdentityDocument: row.ClientIdentityDocument,
        TouristDeclaration: row.TouristDeclaration,
        TouristContractForms: row.TouristContractForms,
        TouristIdentityDocument: row.TouristIdentityDocument,
      };
    },
    add() {
      if (!this.newMunicipality || !this.newMunicipality.Value) return;
      if (this.rows.some((r) => r.MunicipalityId === this.newMunicipality.Value)) {
        this.showError("העיריה כבר קיימת ברשימה");
        return;
      }
      this.isAdding = true;
      const payload = {
        MunicipalityId: this.newMunicipality.Value,
        ClientDeclaration: 0,
        ClientContractForms: 0,
        ClientIdentityDocument: 0,
        TouristDeclaration: 0,
        TouristContractForms: 0,
        TouristIdentityDocument: 0,
      };
      ProfileService.upsertMunicipalityTransferDocument(payload)
        .then((r) => {
          this.rows.push({
            ...r.data,
            MunicipalityName: r.data.MunicipalityName || this.newMunicipality.Text,
            isSaving: false,
            isDeleting: false,
          });
          this.newMunicipality = null;
          this.showSuccess();
        })
        .catch(() => this.showError("שגיאה בהוספת העיריה"))
        .finally(() => {
          this.isAdding = false;
        });
    },
    save(row) {
      row.isSaving = true;
      ProfileService.upsertMunicipalityTransferDocument(this.buildPayload(row))
        .then(() => this.showSuccess())
        .catch(() => this.showError("שגיאה בשמירת מסמכי ההסבה"))
        .finally(() => {
          row.isSaving = false;
        });
    },
    remove(row) {
      this.$buefy.dialog.confirm({
        message: `האם למחוק את הגדרת המסמכים עבור ${row.MunicipalityName}?`,
        confirmText: "מחק",
        cancelText: "בטל",
        type: "is-danger",
        onConfirm: () => {
          row.isDeleting = true;
          ProfileService.deleteMunicipalityTransferDocument(row.MunicipalityId)
            .then(() => {
              this.rows = this.rows.filter((r) => r.MunicipalityId !== row.MunicipalityId);
              this.showSuccess();
            })
            .catch(() => this.showError("שגיאה במחיקת הגדרת המסמכים"))
            .finally(() => {
              row.isDeleting = false;
            });
        },
      });
    },
    showSuccess() {
      this.$buefy.toast.open({ type: "is-success", message: "נשמר בהצלחה!", duration: 4000 });
    },
    showError(message) {
      this.$buefy.toast.open({ type: "is-danger", message, duration: 5000 });
    },
  },
};
</script>

<style lang="scss" scoped>
.hint {
  color: #7a869a;
  font-size: 13px;
  margin-bottom: 10px;
}
.search {
  max-width: 260px;
  margin-bottom: 10px;
}
// Buefy's sticky header uses a fixed 300px height; grow with the table up to the viewport instead,
// leaving room for the navbar, page header, hint, search box and the add-municipality row below.
::v-deep .b-table .table-wrapper.has-sticky-header {
  height: auto;
  max-height: calc(100vh - 350px);

  // Bulma's select arrow sits at z-index 4, above Buefy's sticky th (z-index 2), so it bleeds through the header.
  tr:first-child th {
    z-index: 5;
  }
}
.add-row {
  display: flex;
  align-items: flex-end;
  gap: 10px;
  margin-top: 15px;
}
.add-municipality {
  min-width: 260px;
}
.row-actions {
  display: flex;
  gap: 6px;
  justify-content: center;
}
</style>
